/**
 * B2BAgentPage — Phase 16 (updated Phase 38.4)
 *
 * Route: /b2b/agent  (nested under B2BLayout)
 *
 * Acts as the agent's entry-point router:
 *
 *   ┌─ session not yet seeded ──────────────────────────────────────────┐
 *   │  Show a loading spinner while context is being hydrated.          │
 *   │  Phase 38.4: a 6-second watchdog timer kicks in if session never  │
 *   │  arrives, signing the user out and redirecting to login.          │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 *   ┌─ session.role === 'agent' AND activeClientId === null ────────────┐
 *   │  Render <B2BClientSelection> — the agent picks a client.          │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 *   ┌─ session.role === 'agent' AND activeClientId is set ─────────────┐
 *   │  Navigate to /b2b/catalog — agent is now acting on behalf of the  │
 *   │  selected client; the catalog and cart behave identically to the  │
 *   │  client role.                                                      │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 *   ┌─ session.role === 'client' ────────────────────────────────────────┐
 *   │  Redirect to /b2b/catalog (clients should never land here).        │
 *   └───────────────────────────────────────────────────────────────────┘
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';
import { B2BClientSelection } from './B2BClientSelection';

// How long to wait for the B2BLayout restorer to seed the session before
// giving up and redirecting to the login screen.
const SESSION_TIMEOUT_MS = 6_000;

export function B2BAgentPage() {
  const navigate       = useNavigate();
  const { session }    = useB2BUser();
  const watchdogRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Redirect clients away from this route ──────────────────────────────────
  useEffect(() => {
    if (!session) return;
    if (session.role === 'client') {
      navigate('/b2b/catalog', { replace: true });
      return;
    }
    // Agent with an active client → go straight to catalog
    if (session.role === 'agent' && session.activeClientId !== null) {
      navigate('/b2b/catalog', { replace: true });
    }
  }, [session, navigate]);

  // ── Phase 38.4: watchdog — escape infinite loading when session stays null ──
  // B2BLayout's hard-refresh restorer runs classifyAuthenticatedUser. If the
  // result is 'unknown' it now signs out and navigates to /b2b with an error
  // (Phase 38.4 fix in B2BLayout). But as a belt-and-suspenders defence at the
  // page level, we also start a short timer when session is null. If the session
  // has not been seeded within SESSION_TIMEOUT_MS we sign out ourselves and
  // redirect to login. This catches any future edge cases where a new code path
  // forgets to handle the unknown case.
  useEffect(() => {
    if (session !== null) {
      // Session arrived — cancel any pending watchdog.
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      return;
    }

    // Start the watchdog only once (when session is null on mount or resets).
    watchdogRef.current = setTimeout(() => {
      console.warn('[B2BAgentPage] session not seeded within timeout — signing out');
      supabase.auth.signOut().finally(() => {
        navigate('/b2b', {
          replace: true,
          state: {
            accessError:
              'הגישה נדחתה — פרטי הסוכן לא נמצאו במערכת. ' +
              'צור קשר עם מנהל המערכת כדי לוודא שהפרופיל שלך הוגדר כראוי.',
          },
        });
      });
    }, SESSION_TIMEOUT_MS);

    return () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
  }, [session, navigate]);

  // While context is not yet seeded (e.g. page mount before restorer fires)
  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען…</span>
      </div>
    );
  }

  // Agent with no active client → show client picker
  if (session.role === 'agent' && session.activeClientId === null) {
    return <B2BClientSelection />;
  }

  // Transitioning (useEffect will navigate away)
  return (
    <div className="flex items-center justify-center min-h-[50vh] gap-3 text-gray-400">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">מפנה…</span>
    </div>
  );
}
